/**
 * ============================================================================
 * AUTH PROVIDER
 * ============================================================================
 * 
 * Provides authentication context and handles auth state
 */

import React, { createContext, useEffect, useCallback } from 'react';
import { useAuthStore } from '../store';
import { useAuth } from '../hooks/useAuth';
import { useOnlineStatus } from '../hooks/useOnlineStatus';

const AuthContext = createContext(null);

/**
 * Auth Provider Component
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export const AuthProvider = ({ children }) => {
  const authStore = useAuthStore();
  const authHook = useAuth();
  const isOnline = useOnlineStatus();

  // Sync store with hook
  useEffect(() => {
    if (authStore.isAuthenticated && authStore.user) {
      // User is authenticated, verify token is still valid
      if (authStore.accessToken) {
        // Token exists, check if it needs refresh
        // This would typically check token expiration
      }
    }
  }, [authStore.isAuthenticated, authStore.user, authStore.accessToken]);

  // Auto-refresh token when online
  useEffect(() => {
    if (!isOnline || !authStore.isAuthenticated) return;

    const refreshInterval = setInterval(async () => {
      try {
        // Refresh token if needed (check expiration)
        const token = authStore.accessToken;
        if (token) {
          // Decode token to check expiration (simplified)
          // In production, use jwt-decode library
          const payload = JSON.parse(atob(token.split('.')[1]));
          const expirationTime = payload.exp * 1000;
          const now = Date.now();
          const timeUntilExpiry = expirationTime - now;

          // Refresh if token expires in less than 5 minutes
          if (timeUntilExpiry < 5 * 60 * 1000 && timeUntilExpiry > 0) {
            await authHook.refreshToken();
          }
        }
      } catch (error) {
        console.error('Token refresh error:', error);
      }
    }, 60000); // Check every minute

    return () => clearInterval(refreshInterval);
  }, [isOnline, authStore.isAuthenticated, authStore.accessToken, authHook]);

  // Handle logout on token expiration
  const handleLogout = useCallback(() => {
    authStore.logout();
    authHook.logout();
  }, [authStore, authHook]);

  const value = {
    // Store values
    user: authStore.user,
    isAuthenticated: authStore.isAuthenticated,
    loading: authStore.loading || authHook.loading,
    error: authStore.error || authHook.error,

    // Actions from hook
    login: authHook.loginWithPhone,
    loginWithOTP: authHook.verifyLoginOTP,
    signup: authHook.signupWithPhone,
    signupWithOTP: authHook.verifySignupOTP,
    logout: handleLogout,
    refreshToken: authHook.refreshToken,
    requestPasswordReset: authHook.requestPasswordReset,
    resetPassword: authHook.resetPassword,
    changePassword: authHook.changePassword,
    addEmail: authHook.addEmail,
    verifyEmail: authHook.verifyEmail,
    setPassword: authHook.setPassword,

    // Utility
    isOnline,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };
export default AuthProvider;
